import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

const ZoomableImage = ({ src, fileName }) => {
  const [numPages, setNumPages] = useState(null);
  const [containerWidth, setContainerWidth] = useState(600);
  const containerRef = useRef();

  // Zoom functionality state (for both images and PDFs)
  const [scale, setScale] = useState(1);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [scrollPosition, setScrollPosition] = useState({ x: 0, y: 0 });
  const imageRef = useRef(null);
  const zoomContainerRef = useRef(null);

  const isPDF = fileName && fileName.toLowerCase().endsWith('.pdf');

  useEffect(() => {
    if (containerRef.current) {
      setContainerWidth(containerRef.current.offsetWidth);
    }
  }, []);

  // Zoom functionality - always zooms, uses Ctrl for scrolling
  const handleWheel = useCallback((e) => {
    e.preventDefault();

    // Ctrl/Cmd + wheel = scroll, otherwise = zoom
    if (e.ctrlKey || e.metaKey) {
      // Scroll mode
      if (scale > 1) {
        const container = zoomContainerRef.current;
        if (container) {
          const deltaX = e.shiftKey ? (e.deltaY || e.deltaX) : (e.deltaX || 0);
          const deltaY = e.shiftKey ? 0 : (e.deltaY || 0);

          container.scrollLeft += deltaX;
          container.scrollTop += deltaY;
        }
      }
    } else {
      // Zoom mode
      const delta = e.deltaY > 0 ? 0.9 : 1.1;
      const newScale = Math.min(Math.max(scale * delta, 0.5), 5);
      setScale(newScale);

      // Reset scroll position when zooming out to 1x
      if (newScale === 1) {
        const container = zoomContainerRef.current;
        if (container) {
          container.scrollLeft = 0;
          container.scrollTop = 0;
        }
      }
    }
  }, [scale]);

  const handleMouseDown = useCallback((e) => {
    if (scale <= 1) return;
    e.preventDefault();
    setIsDragging(true);
    const container = zoomContainerRef.current;
    setDragStart({
      x: e.clientX + container.scrollLeft,
      y: e.clientY + container.scrollTop
    });
  }, [scale]);

  const handleMouseMove = useCallback((e) => {
    if (isDragging && scale > 1) {
      const container = zoomContainerRef.current;
      const x = dragStart.x - e.clientX;
      const y = dragStart.y - e.clientY;
      container.scrollLeft = x;
      container.scrollTop = y;
    }
  }, [isDragging, scale, dragStart]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  const zoomIn = () => {
    const newScale = Math.min(scale * 1.2, 5);
    setScale(newScale);
  };

  const zoomOut = () => {
    const newScale = Math.max(scale * 0.8, 0.5);
    setScale(newScale);

    // Reset scroll when zooming out to 1x
    if (newScale === 1) {
      const container = zoomContainerRef.current;
      if (container) {
        container.scrollLeft = 0;
        container.scrollTop = 0;
      }
    }
  };

  const resetZoom = () => {
    setScale(1);
    const container = zoomContainerRef.current;
    if (container) {
      container.scrollLeft = 0;
      container.scrollTop = 0;
    }
  };

  useEffect(() => {
    const container = zoomContainerRef.current;
    if (container) {
      container.addEventListener('wheel', handleWheel, { passive: false });
      container.addEventListener('mousedown', handleMouseDown);
      container.addEventListener('mousemove', handleMouseMove);
      container.addEventListener('mouseup', handleMouseUp);
      container.addEventListener('mouseleave', handleMouseUp);

      return () => {
        container.removeEventListener('wheel', handleWheel);
        container.removeEventListener('mousedown', handleMouseDown);
        container.removeEventListener('mousemove', handleMouseMove);
        container.removeEventListener('mouseup', handleMouseUp);
        container.removeEventListener('mouseleave', handleMouseUp);
      };
    }
  }, [handleWheel, handleMouseDown, handleMouseMove, handleMouseUp]);

  // Zoom Controls Component (reusable)
  const ZoomControls = () => (
    <>
      {/* Zoom Controls */}
      <div style={{
        position: 'absolute',
        top: '10px',
        right: '10px',
        display: 'flex',
        gap: '5px',
        zIndex: 10
      }}>
        <button
          onClick={zoomIn}
          disabled={scale >= 5}
          style={{
            background: scale >= 5 ? 'rgba(0, 0, 0, 0.4)' : 'rgba(0, 0, 0, 0.7)',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            padding: '8px 12px',
            cursor: scale >= 5 ? 'not-allowed' : 'pointer',
            fontSize: '14px',
            fontWeight: 'bold'
          }}
          title="Zoom In"
        >
          +
        </button>
        <button
          onClick={zoomOut}
          disabled={scale <= 0.5}
          style={{
            background: scale <= 0.5 ? 'rgba(0, 0, 0, 0.4)' : 'rgba(0, 0, 0, 0.7)',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            padding: '8px 12px',
            cursor: scale <= 0.5 ? 'not-allowed' : 'pointer',
            fontSize: '14px',
            fontWeight: 'bold'
          }}
          title="Zoom Out"
        >
          −
        </button>
        {scale !== 1 && (
          <button
            onClick={resetZoom}
            style={{
              background: 'rgba(0, 0, 0, 0.7)',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              padding: '8px 12px',
              cursor: 'pointer',
              fontSize: '11px'
            }}
            title="Reset Zoom"
          >
            Reset
          </button>
        )}
      </div>

      {/* Zoom Level Indicator */}
      <div
        style={{
          position: 'absolute',
          bottom: '10px',
          left: '10px',
          background: 'rgba(0, 0, 0, 0.7)',
          color: 'white',
          padding: '6px 10px',
          borderRadius: '4px',
          fontSize: '13px',
          fontWeight: '500'
        }}
      >
        {Math.round(scale * 100)}%
      </div>

      {/* Instruction hint */}
      {scale > 1 && (
        <div
          style={{
            position: 'absolute',
            top: '10px',
            left: '10px',
            background: 'rgba(0, 0, 0, 0.7)',
            color: 'white',
            padding: '6px 10px',
            borderRadius: '4px',
            fontSize: '11px',
            maxWidth: '200px'
          }}
        >
          Drag to pan • Ctrl+Wheel to scroll
        </div>
      )}
    </>
  );

  if (isPDF) {
    return (
      <div
        ref={zoomContainerRef}
        style={{
          width: '100%',
          height: '100%',
          minHeight: '400px',
          maxHeight: '80vh',
          overflow: scale > 1 ? 'auto' : 'hidden',
          position: 'relative',
          cursor: isDragging ? 'grabbing' : scale > 1 ? 'grab' : 'default',
          border: '1px solid #ddd',
          borderRadius: '8px',
          display: 'flex',
          alignItems: scale > 1 ? 'flex-start' : 'center',
          justifyContent: scale > 1 ? 'flex-start' : 'center',
          backgroundColor: '#f5f5f5'
        }}
      >
        <div
          ref={containerRef}
          style={{
            transform: `scale(${scale})`,
            transformOrigin: 'top left',
            transition: isDragging ? 'none' : 'transform 0.1s ease-out',
            userSelect: 'none',
            width: scale > 1 ? `${100 / scale}%` : '100%'
          }}
        >
          <Document
            file={src}
            onLoadSuccess={({ numPages }) => setNumPages(numPages)}
            onLoadError={console.error}
          >
            {Array.from({ length: numPages || 0 }, (_, index) => (
              <Page
                key={`page_${index + 1}`}
                pageNumber={index + 1}
                width={containerWidth * (scale > 1 ? (1 / scale) : 1)}
                renderTextLayer={false}
                renderAnnotationLayer={false}
              />
            ))}
          </Document>
        </div>

        <ZoomControls />
      </div>
    );
  } else if (
    src &&
    (fileName.toLowerCase().endsWith('.jpg') ||
      fileName.toLowerCase().endsWith('.jpeg') ||
      fileName.toLowerCase().endsWith('.png'))
  ) {
    return (
      <div
        ref={zoomContainerRef}
        style={{
          width: '100%',
          height: '100%',
          minHeight: '400px',
          maxHeight: '80vh',
          overflow: scale > 1 ? 'auto' : 'hidden',
          position: 'relative',
          cursor: isDragging ? 'grabbing' : scale > 1 ? 'grab' : 'default',
          border: '1px solid #ddd',
          borderRadius: '8px',
          display: 'flex',
          alignItems: scale > 1 ? 'flex-start' : 'center',
          justifyContent: scale > 1 ? 'flex-start' : 'center'
        }}
      >
        <img
          ref={imageRef}
          src={src}
          alt="Document preview"
          style={{
            maxWidth: scale > 1 ? 'none' : '100%',
            maxHeight: scale > 1 ? 'none' : '100%',
            width: scale > 1 ? `${scale * 100}%` : 'auto',
            objectFit: 'contain',
            userSelect: 'none',
            display: 'block'
          }}
        />

        <ZoomControls />
      </div>
    );
  } else {
    return (
      <object data={src} type="application/pdf" width="95%" height="400px">
        <p>
          Alternative text - include a link <a href={src}>to the PDF!</a>
        </p>
      </object>
    );
  }
};

export default ZoomableImage;